import { useState, useEffect, useCallback } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import { X, Link, MinusCircle } from "lucide-mui";
import { styledComponent } from "../utils/styled";
import { useTheme } from "../context/ThemeContext";
import { documentService } from "../utils/documentService";
import { getValidChunkIds } from "../utils/chunkUtils";
import { updateSources } from "../api/stepsApi";
import { AddFilesModal } from "./AddFilesModalComponent";
import { ConfirmDialog } from "./ui";

// ── Styled components ─────────────────────────────────────────────────────────

const PanelHeader = styledComponent(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "7px",
  height: "30px",
});

const PanelTitle = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "28px",
  color: isDark ? "#fff" : "#31363b",
  letterSpacing: "-0.45px",
}));

const FileList = styledComponent(Box)(({ isDark }) => ({
  maxHeight: "172px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  marginBottom: "11px",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: isDark ? "#1f2123" : "#F0F0F0",
    borderRadius: "99px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: isDark ? "#31363b" : "rgba(0,0,0,0.2)",
    borderRadius: "99px",
  },
}));

const FileRow = styledComponent(Box)(({ isDark }) => ({
  backgroundColor: isDark ? "#1e2022" : "#F5F6F8",
  border: `1px solid ${isDark ? "#31363b" : "#E5E7EB"}`,
  borderRadius: "10px",
  height: "34px",
  display: "flex",
  alignItems: "center",
  gap: "15px",
  padding: "0 8px",
  cursor: "pointer",
  overflow: "hidden",
  "&:hover": { opacity: 0.85 },
}));

const FileIconWrap = styledComponent(Box)(({ isDark }) => ({
  width: 25,
  height: 25,
  backgroundColor: isDark ? "#181a1b" : "#E5E7EB",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const FileName = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "16px",
  color: isDark ? "#fff" : "#31363b",
  flex: 1,
  minWidth: 0,
}));

const DeleteOverlay = styledComponent(Box)({
  position: "absolute",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  paddingRight: "10px",
  pointerEvents: "none",
});

const AddFilesBtn = styledComponent(Box)(({ isSaving }) => ({
  height: "28px",
  width: "91px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: isSaving ? "default" : "pointer",
  background:
    "linear-gradient(162.9deg, rgba(168,174,182,0.5) 0%, rgba(146,153,161,0.5) 50%, rgba(126,132,141,0.5) 100%)",
  border: "1px solid rgba(106,112,121,0.4)",
  borderRadius: "10px",
  opacity: isSaving ? 0.5 : 1,
}));

const AddFilesBtnLabel = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  color: isDark ? "#eceff3" : "#434950",
  letterSpacing: "0.021px",
}));

// ── Component ─────────────────────────────────────────────────────────────────

export function SourceFilesPanel({
  anchorEl,
  open,
  onClose,
  stepData,
  stepNumber,
  sessionId,
  fileId,
  userId,
  anchorId,
  dpmUuid,
  readOnly,
  onDpmUpdate,
  onEditBlocked,
}) {
  const { isDark } = useTheme();
  const [resolvedFiles, setResolvedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const chunkIds = getValidChunkIds(stepData ?? {});

  const resolveFiles = useCallback(async () => {
    if (!chunkIds.length) {
      setResolvedFiles([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const results = await Promise.all(
      chunkIds.map(async chunkId => {
        try {
          const loc = await documentService.getChunkLocation(chunkId, userId);
          return {
            chunkId,
            filename: loc.filename || "Unknown file",
            fileMetaId: loc.fileId,
          };
        } catch {
          return { chunkId, filename: "Unknown file", fileMetaId: null };
        }
      }),
    );
    setResolvedFiles(results);
    setIsLoading(false);
  }, [chunkIds.join(","), userId]);

  useEffect(() => {
    if (open) resolveFiles();
  }, [open, resolveFiles]);

  // Re-resolve when stepData changes (after add/delete)
  useEffect(() => {
    if (open) resolveFiles();
  }, [stepData]);

  const handleDeleteConfirm = async () => {
    if (confirmDeleteIndex === null || isSaving) return;
    setIsSaving(true);
    const newFileIds = resolvedFiles
      .filter((_, i) => i !== confirmDeleteIndex)
      .map(f => f.fileMetaId)
      .filter(id => id != null);
    const result = await updateSources(
      userId,
      sessionId,
      fileId,
      stepNumber,
      newFileIds,
      anchorId,
      dpmUuid,
    );
    if (result.success) onDpmUpdate?.(result.dpm_data);
    else if (result.reason === "dpm_not_draft")
      onEditBlocked?.(result, "This DPM can no longer be edited");
    setConfirmDeleteIndex(null);
    setIsSaving(false);
  };

  const handleAddFiles = async selectedFileIds => {
    setIsSaving(true);
    const existingFileIds = resolvedFiles
      .map(f => f.fileMetaId)
      .filter(id => id != null);
    const newFileIds = [...new Set([...existingFileIds, ...selectedFileIds])];
    const result = await updateSources(
      userId,
      sessionId,
      fileId,
      stepNumber,
      newFileIds,
      anchorId,
      dpmUuid,
    );
    if (result.success) onDpmUpdate?.(result.dpm_data);
    else if (result.reason === "dpm_not_draft")
      onEditBlocked?.(result, "This DPM can no longer be edited");
    setShowAddModal(false);
    setIsSaving(false);
  };

  const handleFileClick = async file => {
    try {
      await documentService.openDocumentInNewTab(file.chunkId, userId);
    } catch (e) {
      console.error("Failed to open document:", e);
    }
  };

  const title = readOnly ? "Source material" : "Source files";
  const existingFileIds = resolvedFiles
    .map(f => f.fileMetaId)
    .filter(id => id != null);

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              background: isDark
                ? "linear-gradient(159.21deg, rgba(49,54,59,0.98) 6.19%, rgba(30,32,34,0.98) 91.81%)"
                : "#FFFFFF",
              border: `1px solid ${isDark ? "rgba(106,112,121,0.5)" : "#E5E7EB"}`,
              borderRadius: "12px",
              p: "8px 6px",
              width: "276px",
              overflow: "visible",
              boxShadow: isDark ? undefined : "0px 8px 32px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <Box>
          <ConfirmDialog
            open={confirmDeleteIndex !== null}
            onCancel={() => setConfirmDeleteIndex(null)}
            onConfirm={handleDeleteConfirm}
            isLoading={isSaving}
            title='Delete File?'
            message='Are you sure you want to delete this file?'
            confirmLabel='Delete'
            danger={false}
            isDark={isDark}
          />

          <PanelHeader>
            <PanelTitle isDark={isDark}>{title}</PanelTitle>
            <IconButton
              onClick={onClose}
              size='small'
              sx={{
                width: 36,
                height: 36,
                color: isDark ? "#b3b3b3" : "#6B7280",
                "&:hover": {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                },
              }}
            >
              <X sx={{ width: 18, height: 18 }} />
            </IconButton>
          </PanelHeader>

          <FileList isDark={isDark}>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} sx={{ color: "#8C8C8C" }} />
              </Box>
            ) : resolvedFiles.length === 0 ? (
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "12px",
                  color: isDark
                    ? "rgba(255,255,255,0.4)"
                    : "rgba(49,54,59,0.4)",
                  textAlign: "center",
                  py: 2,
                  fontStyle: "italic",
                }}
              >
                No source files linked
              </Typography>
            ) : (
              resolvedFiles.map((file, i) => {
                const isOriginal = i === 0;
                const isHovered = hoveredIndex === i;
                return (
                  <Box
                    key={file.chunkId}
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    sx={{ position: "relative", height: "34px", flexShrink: 0 }}
                  >
                    <FileRow
                      isDark={isDark}
                      onClick={() => handleFileClick(file)}
                    >
                      <FileIconWrap isDark={isDark}>
                        <Link
                          sx={{ width: 14, height: 14, color: "#8C8C8C" }}
                        />
                      </FileIconWrap>
                      <FileName isDark={isDark} noWrap>
                        {file.filename}
                      </FileName>
                    </FileRow>

                    {!readOnly && !isOriginal && isHovered && (
                      <DeleteOverlay>
                        <Box
                          component='span'
                          onClick={e => {
                            e.stopPropagation();
                            setConfirmDeleteIndex(i);
                          }}
                          sx={{
                            display: "flex",
                            pointerEvents: "auto",
                            cursor: "pointer",
                          }}
                        >
                          <MinusCircle
                            sx={{
                              width: 17,
                              height: 17,
                              color: isDark ? "#eceff3" : "#434950",
                            }}
                          />
                        </Box>
                      </DeleteOverlay>
                    )}
                  </Box>
                );
              })
            )}
          </FileList>

          {!readOnly && (
            <AddFilesBtn
              isSaving={isSaving}
              onClick={() => !isSaving && setShowAddModal(true)}
            >
              <AddFilesBtnLabel isDark={isDark}>Add files</AddFilesBtnLabel>
            </AddFilesBtn>
          )}
        </Box>
      </Popover>

      <AddFilesModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddFiles}
        userId={userId}
        sessionId={sessionId}
        existingFileIds={existingFileIds}
        isSaving={isSaving}
      />
    </>
  );
}
